import React, { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import type { GroupedVerticalBarProps } from './BarChart.types'
import {
  BAR_CHART_DEFAULT_DOUBLE_BARS,
  BAR_CHART_DEFAULT_HEIGHT,
  BAR_CHART_DEFAULT_TITLE,
  BAR_CHART_DOUBLE_BAR_GAP,
  BAR_CHART_DOUBLE_BAR_WIDTH,
} from './BarChart.types'
import { ChartTooltip } from '../ChartTooltip'
import { getTallestFillAnchor } from './getBarTooltipAnchor'
import './BarChart.css'

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0))

const GroupedVerticalBar: React.FC<GroupedVerticalBarProps> = ({
  title = BAR_CHART_DEFAULT_TITLE,
  hideTitle = false,
  height = BAR_CHART_DEFAULT_HEIGHT,
  bars = BAR_CHART_DEFAULT_DOUBLE_BARS,
  barWidth = BAR_CHART_DOUBLE_BAR_WIDTH,
  gap = BAR_CHART_DOUBLE_BAR_GAP,
  showTooltip = true,
  formatValue,
  darkMode = false,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null)
  const format = formatValue ?? ((v: number) => String(v))

  const resolvedBars = useMemo(
    () => (bars.length > 0 ? bars : BAR_CHART_DEFAULT_DOUBLE_BARS),
    [bars],
  )

  const groupWidth =
    resolvedBars.length * barWidth + Math.max(0, resolvedBars.length - 1) * gap

  const classNames = [
    'ds-bar-chart',
    'ds-bar-chart--double',
    showTooltip ? 'ds-bar-chart--interactive' : '',
    darkMode ? 'ds-bar-chart--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    '--ds-bar-chart-width': `${groupWidth}px`,
    '--ds-bar-chart-height': `${height}px`,
    '--ds-bar-chart-bar-width': `${barWidth}px`,
    '--ds-bar-chart-gap': `${gap}px`,
  } as CSSProperties

  const accessibleLabel =
    ariaLabel ??
    `${title}: ${resolvedBars
      .map((bar) => `${bar.label ?? 'series'} ${clampPercent(bar.value)}%`)
      .join(', ')}`

  return (
    <div
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={accessibleLabel}
      onMouseEnter={
        showTooltip
          ? (event) => {
              setHover(getTallestFillAnchor(event.currentTarget))
            }
          : undefined
      }
      onMouseLeave={showTooltip ? () => setHover(null) : undefined}
    >
      <div className="ds-bar-chart__group" aria-hidden="true">
        {resolvedBars.map((bar, index) => {
          const fillPercent = clampPercent(bar.value)
          return (
            <div
              key={`${bar.label ?? 'bar'}-${index}`}
              className="ds-bar-chart__column"
              style={
                {
                  '--ds-bar-chart-color': bar.color ?? 'var(--ds-blue-600)',
                  '--ds-bar-chart-fill': `${fillPercent}%`,
                } as CSSProperties
              }
            >
              <div
                className={
                  fillPercent >= 100
                    ? 'ds-bar-chart__fill'
                    : 'ds-bar-chart__fill ds-bar-chart__fill--valued'
                }
              />
            </div>
          )
        })}
      </div>
      {!hideTitle ? <p className="ds-bar-chart__title">{title}</p> : null}
      {showTooltip && hover ? (
        <ChartTooltip
          visible
          darkMode={darkMode}
          title={title}
          x={hover.x}
          y={hover.y}
          items={resolvedBars.map((bar, index) => ({
            label: bar.label ?? `Series ${index + 1}`,
            value: format(clampPercent(bar.value)),
            color: bar.color ?? 'var(--ds-blue-600)',
          }))}
        />
      ) : null}
    </div>
  )
}

export default GroupedVerticalBar
